import { Alert, Box, Chip, Paper, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { loadTerminalDiagnostics } from "./api";
import { PageHeader } from "./PageHeader";

export function TerminalDiagnosticsPage({ onBack }: { onBack: () => void }) {
  const { t, i18n } = useTranslation();
  const query = useQuery({ queryKey: ["terminal-diagnostics"], queryFn: loadTerminalDiagnostics,
    refetchInterval: 30_000 });
  return <>
    <PageHeader title={t("administration.entries.terminalDiagnostics")} onBack={onBack}
      description={t("transverse.diagnostic.terminalDescription")} />
    {query.isError && <Alert severity="error" sx={{ mt: 2 }}>{t("transverse.diagnostic.loadError")}</Alert>}
    <Stack spacing={1.5} sx={{ mt: 2 }}>
      {(query.data ?? []).map((item) => {
        const info = item.technicalInfo as Record<string, any>;
        const context = item.businessContext ?? { screen: "", entities: [] };
        const entities = Array.isArray(context.entities) ? context.entities : [];
        return <Paper key={item.id} variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={1}>
            <Box>
              <Typography fontWeight={800}>{item.username || t("transverse.diagnostic.user")}</Typography>
              <Typography variant="body2" color="text.secondary">
                {new Date(item.lastOccurredAt || item.createdAt).toLocaleString(i18n.resolvedLanguage === "en" ? "en-GB" : "fr-FR")} · {item.pageView || item.pagePath}
              </Typography>
            </Box>
            <Stack direction="row" gap={.75} flexWrap="wrap">
              <Chip size="small" label={item.source === "AUTOMATIC"
                ? t("transverse.diagnostic.automatic") : t("transverse.diagnostic.manual")} color={item.source === "AUTOMATIC"
                  ? "warning" : "default"} />
              <Chip size="small" label={item.severity} color={item.severity === "CRITICAL"
                || item.severity === "ERROR" ? "error" : "default"} />
              {item.occurrenceCount > 1 && <Chip size="small" color="warning"
                label={t("transverse.diagnostic.occurrences", { count: item.occurrenceCount })} />}
              <Chip size="small" label={`${info.viewport?.innerWidth ?? "?"}×${info.viewport?.innerHeight ?? "?"}`} />
              <Chip size="small" label={`DPR ${info.devicePixelRatio ?? "?"}`} />
              <Chip size="small" label={`IP ${item.clientIp || t("transverse.diagnostic.unknown")}`} />
              <Chip size="small" label={item.terminalId.slice(0, 8)} />
            </Stack>
          </Stack>
          <Stack direction="row" gap={.75} flexWrap="wrap" sx={{ mt: 1.5 }}>
            <Chip size="small" color="primary" variant="outlined"
              label={t("transverse.diagnostic.screen", { value: context.screen || item.pageView || t("transverse.diagnostic.unidentified") })} />
            {context.action && <Chip size="small" variant="outlined"
              label={`Action · ${context.action}`} />}
            {entities.map((entity, index) => <Chip
              key={`${entity.type}-${entity.identifier}-${index}`} size="small"
              label={`${entity.type} · ${entity.identifier}${entity.status
                ? ` · ${entity.status}` : ""}`} />)}
            {item.fingerprint && <Chip size="small" variant="outlined"
              label={item.fingerprint} />}
          </Stack>
          {item.comment && <Alert severity="info" sx={{ my: 1.5 }}>{item.comment}</Alert>}
          <Typography variant="body2" sx={{ mt: 1, overflowWrap: "anywhere" }}>
            {info.userAgent ?? t("transverse.diagnostic.noUserAgent")}
          </Typography>
          <Typography variant="subtitle2" sx={{ mt: 1.5 }}>
            {t("transverse.diagnostic.events", { count: item.events.length })}
          </Typography>
          <Stack spacing={.5} sx={{ mt: .5 }}>
            {item.events.length === 0 && <Typography color="text.secondary" variant="body2">{t("transverse.diagnostic.noError")}</Typography>}
            {item.events.map((event, index) => <Box key={`${event.at}-${index}`} sx={{
              p: 1, bgcolor: "grey.100", borderRadius: 1, overflowWrap: "anywhere" }}>
              <Typography variant="caption">{event.at} · {event.type}</Typography>
              <Typography variant="body2">{event.message}</Typography>
              {event.detail && <Typography variant="caption" color="text.secondary">{event.detail}</Typography>}
            </Box>)}
          </Stack>
        </Paper>;
      })}
      {query.data?.length === 0 && <Alert severity="info">{t("transverse.diagnostic.empty")}</Alert>}
    </Stack>
  </>;
}
